import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { arrowDown } from '../svg';
import type { Filter } from './entities';

@customElement('umb-market-filter')
export class UmbMarketFilter extends LitElement {
  @property({ type: Object })
  filter?: Filter;

  @property({ type: Boolean, reflect: true })
  active = false;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('filter-cleared', this.#handleFilterClear);
    window.addEventListener('filter-active', this.#handleFilterActive);
    document.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('filter-cleared', this.#handleFilterClear);
    window.removeEventListener('filter-active', this.#handleFilterActive);
    document.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (e: Event) => {
    const target = e
      .composedPath()
      .find(x => (x as HTMLElement).nodeName === 'UMB-MARKET-FILTER');
    if (target !== this) {
      this.active = false;
    }
  };

  #handleFilterActive = (e: Event) => {
    const { detail } = e as CustomEvent;
    if (detail.alias !== this.filter!.alias) {
      this.#handleFilterClear();
    }
  };

  #handleFilterClear = () => {
    this.active = false;
    this.requestUpdate('filter');
  };

  #toggleActive() {
    this.active = !this.active;

    if (this.active) {
      this.dispatchEvent(
        new CustomEvent('filter-active', {
          detail: { alias: this.filter!.alias },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  render() {
    return html` <umb-market-button @click=${this.#toggleActive}>
        <label
          ><span
            >${this.filter?.label}
            ${when(
              this.filter?.tooltip,
              () =>
                html`<umb-market-tooltip
                  ?showtrigger=${true}
                  ?wide=${true}
                  x="left"
                  .text=${this.filter?.tooltip}
                ></umb-market-tooltip>`
            )}</span
          >

          ${when(
            !this.filter?.options[0].selected && !this.active,
            () =>
              html`<span
                >${this.filter?.options.find(x => x.selected)?.name}
              </span>`
          )}
        </label>
        ${arrowDown}
      </umb-market-button>
      <div id="filterBody">
        <slot></slot>
      </div>`;
  }

  static styles = [
    css`
      :host {
        position: relative;
        height: 100%;
        align-self: flex-start;
        box-sizing: border-box;
      }

      #filterBody {
        padding: var(--s-spacer);
        background: var(--uui-color-surface);
      }

      umb-market-button {
        height: 60px;
        justify-content: space-between;
        align-items: center;
        border-top-left-radius: var(--border-top-left-radius, 0);
        border-top-right-radius: var(--border-top-right-radius, 0);
        border-bottom-left-radius: var(--border-bottom-left-radius, 0);
        border-bottom-right-radius: var(--border-bottom-right-radius, 0);
      }

      umb-market-button + div {
        display: none;
      }

      :host([active]) div {
        border-bottom-right-radius: var(--s-spacer) !important;
        border-bottom-left-radius: var(--s-spacer) !important;
        display: block;
      }

      :host([active]) umb-market-button {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      label {
        font-size: 18px;
        font-weight: 400;
        width: 100%;
        line-height: 24px;
        text-align: left;
        cursor: pointer;
      }

      label span {
        display: flex;
        gap: var(--xs-spacer);
      }

      label span:first-child:not(:last-child) {
        font-size: 12px;
        display: flex;
        align-items: center;
        line-height: 1;
        position: absolute;
        transform: translateY(-100%);
        opacity: 0.8;
      }

      label span:first-child:not(:last-child) umb-market-tooltip {
        display: none;
      }

      umb-market-button svg {
        transition: transform cubic-bezier(1, 0, 0, 1) 240ms,
          --fill ease-in-out 0.3s;
        width: 24px;
        height: 24px;
      }

      :host([active]) svg,
      umb-market-button:hover svg {
        --fill: var(--uui-color-default);
      }

      :host([active]) label + svg {
        transform: rotate(180deg);
      }

      input,
      select {
        width: 100%;
      }
    `,
  ];
}
